// RehabDetailsTab.tsx

import React, { useEffect, useMemo, useState, useCallback } from "react"
import { View, ActivityIndicator } from "react-native"
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view"
import BFText, { Body, H4, H5, Small } from "@/components/standard/BFText"
import { SecondaryButton } from "@/components/buttons/CommonButtons"
import { Vertical } from "@/components/standard/BFLayout"
import { type RehabSowTabs } from "@/screens/rehab/RehabSowScreen"
import BFDropDown from "@/components/standard/BFDropDown"
import {
  GENERIC_SELECT_TEXT,
  PROPERTY_TYPE_OPTIONS,
  YES_NO_CHANGE_OPTIONS,
} from "@/screens/rehab/utils/Rehab"
import { useLoanContext } from "@/screens/loans/components/LoanContext"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import { type DropdownOption } from "@/types/dropdownTypes"
import { cn } from "@/components/util/cn"
import { TextFieldWithLabel } from "@/components/forms/FieldWithLabel"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { usePhoneNumberInput } from "@/hooks/usePhoneNumberInput"
import { useUser } from "@/queries/user"
import { useConditionalInput } from "@/hooks/useConditionalInput"
import { BFColors } from "@/constants/Colors"
import { useRehabBudgetingFeature } from "@/hooks/features/useRehabBudgetingFeature"
import Animated from "react-native-reanimated"
import useUpdateDimming from "@/hooks/useUpdateDimming"
import { useEmailInput } from "@/hooks/useEmailInput"
import { MiniRequirementMessage } from "@/screens/rehab/components/MiniRequirementMessage"

export const RehabDetailsTab = ({
  setSelectedTab,
  rehabBudgetId,
}: {
  setSelectedTab: (tab: RehabSowTabs) => void
  rehabBudgetId: string
}) => {
  const { data: user } = useUser()
  const userProfile = useMemo(() => user?.me, [user])
  const fullName = `${userProfile?.firstName} ${userProfile?.lastName}`.trim()

  const isDesktop = useIsDesktop()

  const { loanApplication } = useLoanContext()
  const [loading, setLoading] = useState<boolean>(false)

  // ** Email **
  const memberEmail = userProfile?.email || ""

  // ** Pseudo State **
  const [pseudoSaveSelected, setPseudoSaveSelected] = useState<boolean>(false)

  // Prepopulate details from the backend.
  const {
    rehabBudget,
    editRehabDetails,
    setInitialBudgetPropertyType,
    updatePropertyDetails,
    updatePrimaryDrawsContact,
    existingPropertyType,
    latestPropertyData,
    isSaving,
    isLoading: isRehabLoading,
    error: rehabError,
    isEditable,
  } = useRehabBudgetingFeature(loanApplication, isDesktop)

  // **State for YES/NO Changes**
  const [homeChanges, setHomeChanges] = useState<boolean | null>(true)
  const [usingGC, setUsingGC] = useState<boolean | null>(
    rehabBudget?.primaryDrawsContact?.type === "gc"
  )

  // **State for Property Type & Details**
  const [details, setDetails] = useState(latestPropertyData)

  const [selectedExistingPropertyType, setSelectedExistingPropertyType] =
    useState<string | null | undefined>(() => {
      // Case-insensitive matching for the initial value
      const matchOption = PROPERTY_TYPE_OPTIONS.find(
        (option) => option.value === existingPropertyType || ""
      )
      return (matchOption?.value as string | null | undefined) || null
    })

  const [selectedRenovatedPropertyType, setSelectedRenovatedPropertyType] =
    useState<string | null | undefined>(() => {
      const matchOption = PROPERTY_TYPE_OPTIONS.find(
        (option) => option.value === latestPropertyData.propertyType || ""
      )
      return (matchOption?.value as string | null | undefined) || null
    })

  const [projectDescription, setProjectDescription] = useState<string>(
    rehabBudget?.projectDescription || ""
  )

  // Use the dimming hook from the Comps tab
  const { animatedDimStyle } = useUpdateDimming(isSaving)

  // **Handle Home Changes Selection**
  const handleHomeChangesChange = (value: boolean | undefined) => {
    if (!isEditable) return
    setHomeChanges(value ?? null)
    saveGeneralDetails()
  }

  // ** Handle GC Changes Selection**
  const handleGeneralContractorChange = (value: boolean | undefined) => {
    if (!isEditable) return
    setUsingGC(value ?? null)
    saveGeneralDetails()
  }

  const savePropertyDetails = useCallback(() => {
    if (!isEditable) return
    // Get initial values
    const { initialPropertyDetails, modifiedPropertyDetails } =
      rehabBudget || {}

    // If Home Changes is set to "No", revert to initialPropertyDetails
    let updatedPropertyDetails = { ...details }

    if (homeChanges === false && initialPropertyDetails) {
      updatedPropertyDetails = {
        squareFootage:
          initialPropertyDetails.propertySqFt ??
          modifiedPropertyDetails?.propertySqFt ??
          details.squareFootage,
        bedroomCount:
          initialPropertyDetails.propertyBeds ??
          modifiedPropertyDetails?.propertyBeds ??
          details.bedroomCount,
        bathroomCount:
          Number(initialPropertyDetails.propertyBaths) ??
          modifiedPropertyDetails?.propertyBaths ??
          details.bathroomCount,
        propertyType:
          initialPropertyDetails.propertyType ??
          modifiedPropertyDetails?.propertyType ??
          details.propertyType,
      }
    }

    const backendPropertyType =
      PROPERTY_TYPE_OPTIONS.find(
        (option) => option.label === details.propertyType
      )?.value ?? details.propertyType

    updatePropertyDetails({
      rehabBudgetId,
      propertySqFt: updatedPropertyDetails.squareFootage,
      propertyBeds: updatedPropertyDetails.bedroomCount,
      propertyBaths: updatedPropertyDetails.bathroomCount,
      propertyType: backendPropertyType,
    })
  }, [
    isEditable,
    loanApplication.rehabBudget?.id,
    details,
    homeChanges,
    updatePropertyDetails,
    rehabBudget,
    selectedRenovatedPropertyType,
  ])

  const {
    phoneNumber: gcPhoneNumber,
    onPhoneChange,
    // phoneNumberError,
    isValid: isPhoneValid,
    validatePhoneNumber,
  } = usePhoneNumberInput("", () => {
    console.log("Phone number saved successfully.")
  })

  const {
    isEmailValid,
    email: contactEmail,
    setEmail: setContactEmail,
  } = useEmailInput("")

  // * General Contractor Name Input Hook *
  const { value: gcName, onValueChange: setGCName } = useConditionalInput(
    fullName,
    usingGC === false
  )

  const primaryDrawsContact = useMemo(
    () => ({
      name: gcName.trim(),
      email: contactEmail.trim(),
      phone: gcPhoneNumber?.trim() || null,
    }),
    [gcName, contactEmail, gcPhoneNumber]
  )

  // ** SAVING **
  const saveGeneralDetails = useCallback(() => {
    if (!rehabBudget?.id || !isEditable) return

    updatePrimaryDrawsContact({
      rehabBudgetId,
      name: primaryDrawsContact.name.trim(),
      email: primaryDrawsContact.email.trim(),
      phone: primaryDrawsContact.phone?.trim() || "",
      isGc: usingGC,
    })

    if (rehabBudget?.projectDescription !== projectDescription) {
      editRehabDetails({
        rehabBudgetId,
        rehabInput: { projectDescription },
      })
    }
  }, [
    isEditable,
    rehabBudget?.id,
    primaryDrawsContact,
    projectDescription,
    rehabBudget,
    editRehabDetails,
    updatePrimaryDrawsContact,
    usingGC,
  ])

  // ** Validation **

  const isExistingPropertyTypeValid = !!selectedExistingPropertyType
  const isRenovatedPropertyTypeValid = true

  const areRenovatedPropertyDetailsValid =
    (homeChanges === true &&
      !!details.propertyType &&
      !!details.squareFootage &&
      !!details.bedroomCount &&
      !!details.bathroomCount) ||
    homeChanges === false
  const isNameValid = gcName.trim() !== ""
  const isProjectDescriptionValid = projectDescription.trim() !== ""

  const requirementsAreValid: boolean =
    isProjectDescriptionValid &&
    isNameValid &&
    isEmailValid &&
    isExistingPropertyTypeValid &&
    isRenovatedPropertyTypeValid &&
    areRenovatedPropertyDetailsValid

  // ** Effect Hooks **

  useEffect(() => {
    setHomeChanges(!!rehabBudget?.hasModifiedPropertyDetails)
  }, [rehabBudget?.hasModifiedPropertyDetails])

  useEffect(() => {
    validatePhoneNumber()
    // validateEmail() removed as email now uses conditional hook
  }, [gcPhoneNumber, validatePhoneNumber, contactEmail])

  // Prepopulate property details from backend if available
  useEffect(() => {
    if (rehabBudget) {
      if (rehabBudget.modifiedPropertyDetails) {
        setDetails(latestPropertyData)
      }
      if (rehabBudget.projectDescription) {
        setProjectDescription(rehabBudget.projectDescription)
      }

      if (rehabBudget.primaryDrawsContact) {
        setGCName(rehabBudget.primaryDrawsContact.name || gcName)
        setContactEmail(rehabBudget.primaryDrawsContact.email || contactEmail)
        // setGCEmail(rehabBudget.primaryDrawsContact.email || gcEmail)
        onPhoneChange(rehabBudget.primaryDrawsContact.phone || "")
      }
    }
  }, [rehabBudget, loanApplication, setContactEmail])

  useEffect(() => {
    validatePhoneNumber()
  }, [gcPhoneNumber, validatePhoneNumber, contactEmail])

  useEffect(() => {
    if (usingGC === false && contactEmail.trim() === "") {
      setContactEmail(memberEmail)
    }
  }, [usingGC])

  useEffect(() => {
    if (details.propertyType) {
      savePropertyDetails()
    }
  }, [details.propertyType]) // Trigger only when property type changes

  // Handlers for input changes.
  const handleExistingPropertyTypeChange = (newValue: string | undefined) => {
    if (!isEditable) return
    if (!newValue) return

    setSelectedExistingPropertyType(newValue)
    setDetails((prev) => ({
      ...prev,
      propertyType:
        PROPERTY_TYPE_OPTIONS.find((option) => option.value === newValue)
          ?.value || "",
    }))

    setInitialBudgetPropertyType({
      rehabBudgetId,
      propertyType: newValue,
    })
  }

  const handleRenovatedPropertyTypeChange = (newValue: string | undefined) => {
    if (!isEditable) return
    if (!newValue) return

    setSelectedRenovatedPropertyType(newValue)
    setDetails((prev) => ({
      ...prev,
      propertyType:
        PROPERTY_TYPE_OPTIONS.find((option) => option.value === newValue)
          ?.value || "",
    }))

    savePropertyDetails()
  }

  const handleInputChange = (key: keyof typeof details, value: number) => {
    if (!isEditable) return
    setDetails((prev) => ({ ...prev, [key]: value }))
  }

  const onNext = () => {
    setLoading(true)
    saveAllDetails()
    if (!requirementsAreValid) {
      setPseudoSaveSelected(true)
      return
    }
    setTimeout(() => {
      setSelectedTab("summary")
      setLoading(false)
    }, 1000)
  }

  // Define the save function that compiles all details
  const saveAllDetails = () => {
    if (!isEditable) return
    savePropertyDetails()
    saveGeneralDetails()
  }

  if (isRehabLoading) {
    return <ActivityIndicator size="small" />
  }
  if (rehabError) {
    return <BFText>Error loading rehab details</BFText>
  }

  const homeChangesOkayToEdit = homeChanges && isEditable

  return (
    <KeyboardAwareScrollView contentContainerStyle={{ paddingBottom: 20 }}>
      <Animated.View
        style={animatedDimStyle}
        className="flex-1 bg-off-white w-full"
      >
        <Vertical className="gap-0 my-0">
          {/* Home Changes */}

          <H4 className="text-darkest mt-3 mb-5">Home Changes</H4>
          <TitleWithAsterisk title={"Existing Property Type"} />

          <BFDropDown
            fullWidth={true}
            options={PROPERTY_TYPE_OPTIONS}
            value={selectedExistingPropertyType || null}
            readonlyText={""}
            onValueChange={handleExistingPropertyTypeChange}
            placeholder={GENERIC_SELECT_TEXT}
            disabled={!isEditable}
            zIndex={1}
          />

          <MiniRequirementMessage
            message={"You must select an existing property type"}
            displays={!isExistingPropertyTypeValid}
            pseudoEnabled={pseudoSaveSelected}
          />

          <TitleWithAsterisk className={"mt-4"} title={"Renovated Property"} />
          <Small className="mb-2">
            Will you be changing the total square footage, bedroom count,
            bathroom count, or property type?
          </Small>
        </Vertical>

        {/* **Home Changes Dropdown** */}
        <BFDropDown
          options={YES_NO_CHANGE_OPTIONS}
          value={homeChanges}
          onValueChange={handleHomeChangesChange}
          placeholder={GENERIC_SELECT_TEXT}
          fullWidth={true}
          disabled={!isEditable}
          zIndex={3}
        />

        <MiniRequirementMessage
          message={"You must select yes or no"}
          displays={!isRenovatedPropertyTypeValid}
          pseudoEnabled={pseudoSaveSelected}
        />

        {/* Input Fields */}
        <Vertical style={{ zIndex: 2 }}>
          <Small className="mt-4 mb-2">
            Confirm the final number for the following. If there are changes,
            please update in the cell field.
          </Small>

          <View
            className={cn(
              "border-light-grey bg-lightest border rounded-[6px] pt-5 px-5",
              homeChanges ? "pb-5" : ""
            )}
          >
            {/* Two-column layout */}
            <CalculatorItem
              title="Est Sq Ft"
              variant={homeChanges ? "default" : "tight"}
              prefix={""}
              subtitle={
                homeChanges
                  ? `(${loanApplication?.property?.area || "0"} Sq Ft)`
                  : undefined
              }
              value={details.squareFootage}
              onValueChange={
                homeChangesOkayToEdit
                  ? (value) => {
                      handleInputChange("squareFootage", value)
                    }
                  : undefined
              }
              onBlur={
                homeChangesOkayToEdit
                  ? () => {
                      savePropertyDetails()
                    }
                  : undefined
              }
            />
            <CalculatorItem
              title="# of Beds"
              variant={homeChanges ? "default" : "tight"}
              prefix={""}
              subtitle={
                homeChanges
                  ? `(${loanApplication?.property?.bedrooms || "0"} Beds)`
                  : undefined
              }
              value={details.bedroomCount}
              onValueChange={
                homeChangesOkayToEdit
                  ? (value) => {
                      handleInputChange("bedroomCount", value)
                    }
                  : undefined
              }
              onBlur={
                homeChangesOkayToEdit
                  ? () => {
                      savePropertyDetails()
                    }
                  : undefined
              }
            />
            <CalculatorItem
              title="# of Baths"
              variant={homeChanges ? "default" : "tight"}
              prefix={""}
              precision={1}
              subtitle={
                homeChanges
                  ? `(${loanApplication?.property?.bathrooms || "0"} Baths)`
                  : undefined
              }
              value={details.bathroomCount}
              onValueChange={
                homeChangesOkayToEdit
                  ? (value) => {
                      handleInputChange("bathroomCount", value)
                    }
                  : undefined
              }
              onBlur={
                homeChangesOkayToEdit
                  ? () => {
                      savePropertyDetails()
                    }
                  : undefined
              }
            />

            {/* Reno Property Type Dropdown */}
            <DetailDropdownField
              label="Property Type"
              options={PROPERTY_TYPE_OPTIONS}
              value={selectedRenovatedPropertyType}
              onValueChange={
                homeChanges ? handleRenovatedPropertyTypeChange : undefined
              }
              disabled={!isEditable}
              placeholder={GENERIC_SELECT_TEXT}
              zIndex={2}
            />
          </View>
          <MiniRequirementMessage
            message={"You must enter all renovated property details"}
            displays={!areRenovatedPropertyDetailsValid}
            pseudoEnabled={pseudoSaveSelected}
          />
        </Vertical>

        {/* GC Title */}
        <H4 className="text-darkest mt-6 mb-2">General Contractor</H4>
        <Small className="mb-2">Will you be hiring a general contractor?</Small>

        {/* General Contractor Dropdown */}
        <BFDropDown
          options={YES_NO_CHANGE_OPTIONS}
          value={usingGC}
          onValueChange={handleGeneralContractorChange}
          placeholder={GENERIC_SELECT_TEXT}
          fullWidth={true}
          disabled={!isEditable}
          zIndex={1}
        />
        <Small className="mt-4 mb-2">
          If “no” licensed GC, please list the name of who will be managing the
          draws.
        </Small>

        {/* GC Name Input - Only Show When Self-Managing */}
        <View className={cn("flex flex-col", !isDesktop && "flex-1 mt-4")}>
          {/* NAME INPUT */}
          <TextFieldWithLabel
            label="Name"
            descriptor={usingGC ? "(General Contractor)" : "(Self Managed)"}
            required
            validator={(value) => {
              return !value || value.trim().length < 1
                ? "Contractor name is required"
                : undefined
            }}
            textFieldProps={{
              value: gcName,
              onChangeText: setGCName,
              returnKeyType: "done",
              returnKeyLabel: "Done",
              autoCorrect: false,
              textContentType: "name",
              autoComplete: "name",
              testID: "gc-name-text-input",
              style: {
                backgroundColor: BFColors.lightest,
              },
              onBlur: saveGeneralDetails,
              editable: isEditable,
            }}
          />

          <MiniRequirementMessage
            message={"You must enter a name"}
            displays={!isNameValid}
            className={"mb-5"}
            pseudoEnabled={pseudoSaveSelected}
          />

          {/* EMAIL INPUT */}
          <TextFieldWithLabel
            label={"Email"}
            initialValue={contactEmail}
            textFieldProps={{
              testID: "gc-email-text-input",
              returnKeyType: "done",
              returnKeyLabel: "Done",
              keyboardType: "email-address",
              // these three lines to block capital letters & suggestions:
              autoCapitalize: "none",
              autoCorrect: false,
              textContentType: "emailAddress", // iOS “smart” hints for email fields
              autoComplete: "email",
              value: contactEmail || "",
              onChangeText: setContactEmail,
              style: {
                backgroundColor: BFColors.lightest,
              },
              onBlur: saveGeneralDetails,
              editable: isEditable,
            }}
            required
            validator={(value) => {
              return value.trim() === "" ? "Invalid email address" : ""
            }}
          />

          <MiniRequirementMessage
            message={"You must enter a valid email"}
            displays={!isEmailValid}
            className={"mb-5"}
            pseudoEnabled={pseudoSaveSelected}
          />

          <TextFieldWithLabel
            label={"Phone Number"}
            initialValue={"(___)-___-____"}
            textFieldProps={{
              testID: "gc-phone-text-input",
              keyboardType: "phone-pad",
              placeholder: "(___) ___-____",
              returnKeyType: "done",
              returnKeyLabel: "Done",
              value: gcPhoneNumber || "",
              onChangeText: onPhoneChange,
              style: {
                backgroundColor: BFColors.lightest,
              },
              onBlur: saveGeneralDetails,
              editable: isEditable,
            }}
            validator={(value) =>
              value.length < 1 || !isPhoneValid ? "Invalid phone number" : ""
            }
            dark={false}
          />

          <MiniRequirementMessage
            message={"You must enter a valid phone number"}
            displays={!isPhoneValid && gcPhoneNumber !== ""}
            className={"mb-5"}
            pseudoEnabled={pseudoSaveSelected}
          />

          <TextFieldWithLabel
            multiline={true}
            required
            showRequiredIndicator={true}
            label={"Project Description"}
            description={"Enter a brief summary of your project"}
            initialValue={projectDescription}
            submitBehavior={"blurAndSubmit"}
            textFieldProps={{
              testID: "project-description-text-input",
              returnKeyType: "done",
              returnKeyLabel: "Done",
              placeholder: "Description...",
              value: projectDescription,
              onChangeText: setProjectDescription,
              style: {
                backgroundColor: BFColors.lightest,
              },
              onBlur: saveGeneralDetails,
              editable: isEditable,
            }}
          />

          <MiniRequirementMessage
            message={"You must enter a project description"}
            displays={!isProjectDescriptionValid}
            className={"mb-5"}
            pseudoEnabled={pseudoSaveSelected}
          />
        </View>

        {/* Next Button */}
        <View className="mb-5 mt-8">
          <SecondaryButton
            testID="rehab-details-next"
            loading={loading || isSaving}
            title="Next >"
            pressOnceOnly={true}
            onPress={onNext}
            pseudoDisabled={!requirementsAreValid}
            onPseudoPress={() => {
              setPseudoSaveSelected(true)
            }}
          />
        </View>
      </Animated.View>
    </KeyboardAwareScrollView>
  )
}

// **Reusable Dropdown Field**
export const DetailDropdownField = <T extends string | boolean>({
  label,
  helperText,
  options,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  zIndex,
}: {
  label: string
  helperText?: string
  options: Array<DropdownOption<T>>
  value: T | null | undefined
  onValueChange?: (value: T | undefined) => void
  placeholder: string
  disabled?: boolean
  zIndex: number
}) => (
  <View className="mt-4 flex-row justify-between">
    <View>
      <Body className="text-midnight-blue">{label}</Body>
      {helperText && <Small className="text-midnight-blue">{helperText}</Small>}
    </View>
    <BFDropDown<T>
      options={options}
      value={value || null}
      onValueChange={onValueChange}
      placeholder={placeholder}
      fullWidth={false}
      disabled={disabled}
      zIndex={zIndex}
    />
  </View>
)

// Local component for H4 titles with a red asterisk appended
const TitleWithAsterisk = ({
  title,
  className = "",
}: {
  title: string
  className?: string
}) => (
  <View className={cn(className, "flex-row mb-1")}>
    <H5>{title}</H5>
    <Body className="text-red-orange ml-1">*</Body>
  </View>
)
